import { addDaysToDate, addMonthsToDate, compareDates, daysBetween, monthEndAfter, parseDate, } from '../dateString.js';
import { defined } from '../defined.js';
import { withinCap } from './constructors.js';
import { firstOccurrence, } from './dsl.js';
const MAX_PAYMENT_OCCURRENCES = 100;
function formatDateInternal(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function evaluateDateObservable(obs, ctx) {
    switch (obs.kind) {
        case 'const':
            return obs.value;
        case 'context':
            return ctx.dates?.[obs.dateName];
        case 'startOfMonth': {
            const d = parseDate(ctx.currentDate);
            d.setMonth(d.getMonth() + obs.monthOffset);
            d.setDate(1);
            return formatDateInternal(d);
        }
        case 'endOfMonth': {
            const d = parseDate(ctx.currentDate);
            d.setMonth(d.getMonth() + obs.monthOffset + 1);
            d.setDate(0);
            return formatDateInternal(d);
        }
        case 'dayOfMonth': {
            const d = parseDate(ctx.currentDate);
            d.setMonth(d.getMonth() + obs.monthOffset);
            d.setDate(obs.day);
            return formatDateInternal(d);
        }
        case 'addDays': {
            const base = evaluateDateObservable(obs.base, ctx);
            return base === undefined ? undefined : addDaysToDate(base, obs.days);
        }
    }
}
function evaluateNumericObservable(obs, ctx) {
    switch (obs.kind) {
        case 'const':
            return obs.value;
        case 'daysLate': {
            if (!ctx.dueDate)
                return 0;
            const daysLate = daysBetween(ctx.dueDate, ctx.currentDate) - obs.gracePeriod;
            return Math.max(0, daysLate);
        }
        case 'context':
            return ctx.measures?.[obs.factName] ?? 0;
        case 'multiply':
            return evaluateNumericObservable(obs.left, ctx) * evaluateNumericObservable(obs.right, ctx);
        case 'max':
            if (obs.values.length === 0) {
                throw new Error(`Cannot evaluate 'max' of empty values array`);
            }
            return Math.max(...obs.values.map((v) => evaluateNumericObservable(v, ctx)));
        case 'min':
            if (obs.values.length === 0) {
                throw new Error(`Cannot evaluate 'min' of empty values array`);
            }
            return Math.min(...obs.values.map((v) => evaluateNumericObservable(v, ctx)));
    }
}
function evaluateCondition(cond, ctx) {
    switch (cond.kind) {
        case 'const':
            return cond.value;
        case 'after': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) > 0;
        }
        case 'before': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) < 0;
        }
        case 'onOrAfter': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) >= 0;
        }
        case 'onOrBefore': {
            const date = evaluateDateObservable(cond.date, ctx);
            return date !== undefined && compareDates(ctx.currentDate, date) <= 0;
        }
        case 'isLate': {
            if (!ctx.dueDate)
                return false;
            const daysLate = daysBetween(ctx.dueDate, ctx.currentDate);
            return daysLate > cond.gracePeriod;
        }
        case 'and':
            return cond.conditions.every((c) => evaluateCondition(c, ctx));
        case 'or':
            return cond.conditions.some((c) => evaluateCondition(c, ctx));
        case 'not':
            return !evaluateCondition(cond.condition, ctx);
        case 'context':
            return ctx.facts?.[cond.factName] ?? false;
    }
}
function evaluateAmountSpec(spec, ctx) {
    switch (spec.kind) {
        case 'unit':
            return { amount: 1, isConditional: ctx.isConditional ?? false };
        case 'scale': {
            const factor = evaluateNumericObservable(spec.factor, ctx);
            const inner = evaluateAmountSpec(spec.amount, ctx);
            return { ...inner, amount: inner.amount * factor };
        }
        case 'give': {
            const inner = evaluateAmountSpec(spec.amount, ctx);
            return { ...inner, amount: -inner.amount };
        }
        case 'when': {
            const conditionMet = evaluateCondition(spec.condition, ctx);
            const conditionalCtx = { ...ctx, isConditional: true };
            const inner = evaluateAmountSpec(spec.amount, conditionalCtx);
            return { ...inner, amount: conditionMet ? inner.amount : 0 };
        }
        case 'all': {
            let totalAmount = 0;
            let description;
            let classification;
            let isConditional = ctx.isConditional ?? false;
            for (const amount of spec.amounts) {
                const result = evaluateAmountSpec(amount, ctx);
                totalAmount += result.amount;
                if (result.description && !description)
                    description = result.description;
                if (result.classification && !classification)
                    classification = result.classification;
                if (result.isConditional)
                    isConditional = true;
            }
            return {
                amount: totalAmount,
                ...defined({ description, classification }),
                isConditional,
            };
        }
        case 'first': {
            for (const amount of spec.amounts) {
                const result = evaluateAmountSpec(amount, ctx);
                if (result.amount !== 0) {
                    return result;
                }
            }
            return { amount: 0, isConditional: ctx.isConditional ?? false };
        }
        case 'capped': {
            const maxAmount = evaluateNumericObservable(spec.maxAmount, ctx);
            const inner = evaluateAmountSpec(spec.amount, ctx);
            return { ...inner, amount: Math.min(inner.amount, maxAmount) };
        }
        case 'describe': {
            const inner = evaluateAmountSpec(spec.amount, ctx);
            return {
                ...inner,
                description: spec.description,
                ...defined({ classification: spec.classification }),
            };
        }
        case 'surcharge': {
            const rate = evaluateNumericObservable(spec.rate, ctx);
            const inner = evaluateAmountSpec(spec.amount, ctx);
            return { ...inner, amount: inner.amount + inner.amount * rate };
        }
    }
}
export function collectPayments(payment, ctx, windowEnd) {
    switch (payment.kind) {
        case 'zero':
            return [];
        case 'once': {
            const dueDate = evaluateDateObservable(payment.date, ctx);
            if (dueDate === undefined)
                return [];
            if (compareDates(dueDate, windowEnd) > 0)
                return [];
            const amountCtx = { ...ctx, dueDate };
            const result = evaluateAmountSpec(payment.amount, amountCtx);
            return [
                {
                    dueDate,
                    amount: result.amount,
                    ...defined({ description: result.description, classification: result.classification }),
                    isConditional: result.isConditional,
                },
            ];
        }
        case 'all':
            return payment.payments.flatMap((p) => collectPayments(p, ctx, windowEnd));
        case 'first': {
            for (const p of payment.payments) {
                const results = collectPayments(p, ctx, windowEnd);
                if (results.length > 0)
                    return results;
            }
            return [];
        }
        case 'repeat': {
            const results = [];
            const start = evaluateDateObservable(payment.startDate, ctx);
            if (start === undefined)
                return [];
            let currentStart = firstOccurrence(payment.recurrence, start);
            const end = payment.endDate ?? windowEnd;
            let occurrences = 0;
            const countsHistory = payment.maxOccurrences !== undefined;
            const maxOccurrences = payment.maxOccurrences ?? MAX_PAYMENT_OCCURRENCES;
            while (compareDates(currentStart, end) <= 0 &&
                compareDates(currentStart, windowEnd) <= 0 &&
                occurrences < maxOccurrences) {
                const occurrenceCtx = {
                    ...ctx,
                    dueDate: currentStart,
                };
                const result = evaluateAmountSpec(payment.amount, occurrenceCtx);
                if (result.amount !== 0) {
                    results.push({
                        dueDate: currentStart,
                        amount: result.amount,
                        ...defined({ description: result.description, classification: result.classification }),
                        isConditional: result.isConditional,
                    });
                }
                if (countsHistory || compareDates(currentStart, ctx.currentDate) >= 0) {
                    occurrences++;
                }
                let next = currentStart;
                switch (payment.recurrence.kind) {
                    case 'once':
                        next = addDaysToDate(end, 1);
                        break;
                    case 'daily':
                        next = addDaysToDate(currentStart, payment.recurrence.interval);
                        break;
                    case 'weekly':
                        next = addDaysToDate(currentStart, 7 * payment.recurrence.interval);
                        break;
                    case 'monthly':
                        next = addMonthsToDate(currentStart, payment.recurrence.interval);
                        break;
                    case 'yearly':
                        next = addMonthsToDate(currentStart, 12 * payment.recurrence.interval);
                        break;
                    case 'monthEnd':
                        next = monthEndAfter(currentStart, payment.recurrence.interval);
                        break;
                }
                if (compareDates(next, currentStart) <= 0)
                    break;
                currentStart = next;
            }
            return results;
        }
        case 'notToExceed':
            return withinCap(collectPayments(payment.payment, ctx, windowEnd), payment.maxAmount, payment.period);
    }
}
//# sourceMappingURL=occurrences.js.map